import i18n from 'i18next';

/**
 * Format a cents amount as USD, returning "Free" when the amount is 0 (or
 * unset). Use this for any user-facing price or order total — the listing
 * price and the order's totalPaid both treat 0 as "this is a free dish".
 *
 * Money is never rounded: the output always reflects the exact cents value.
 * Default output is cents-precise ("$12.34", "$12.00").
 *
 * Pass `{ dropZeroCents: true }` on listing-price displays to render a
 * whole-dollar amount as "$12" instead of "$12.00". This is lossless — any
 * non-zero cents still render ("$8.50"), so a bad price can never be hidden.
 * Listing prices are whole dollars by invariant (price wheel + BE guard), so
 * those sites read "$12" in practice.
 *
 * Pass `{ zeroAsAmount: true }` where 0 means "no money yet" rather than "this
 * is free", such as a running total or balance. The My Earnings header reads
 * "$0.00" for a chef with no settled sales; "Free" would be nonsense there.
 *
 * Prices are always USD (the platform charges in USD), so the symbol stays
 * fixed; only the "Free" word is user-language copy from `common`.
 */
export const formatPriceCents = (
  cents: number | null | undefined,
  options?: { dropZeroCents?: boolean; zeroAsAmount?: boolean },
): string => {
  if (cents == null || cents <= 0) {
    if (options?.zeroAsAmount) return '$0.00';
    return i18n.t('price.free', { ns: 'common' });
  }
  if (options?.dropZeroCents && cents % 100 === 0) return `$${cents / 100}`;
  return `$${(cents / 100).toFixed(2)}`;
};
